import { unstable_batchedUpdates } from 'react-dom';
import { matchPath } from 'react-router-dom';
import { devtools, subscribeWithSelector } from 'zustand/middleware';
import { shallow } from 'zustand/shallow';
import { createWithEqualityFn } from 'zustand/traditional';

import { history } from '../history';
import { athomCloudApi } from '../lib/athomCloudApi';
import { HomeyError } from '../lib/InternalError';

import { BaseStore } from './BaseStore';
import { selectedHomeyStore } from './SelectedHomeyStore';

class AuthStore extends BaseStore {
  createInitialState() {
    return {
      cloudUser: null,
      localUser: null,
      experimentalFlag: false,
      employeeFlag: false,
      loading: true,
      error: null,
      resolved: false,
    };
  }

  store = createWithEqualityFn(
    devtools(
      subscribeWithSelector((set, get, api) => ({
        ...this.createInitialState(),
      })),
      { name: 'auth' },
    ),
    shallow,
  );

  async getAuthenticatedCloudUserState() {
    const cloudUser = await athomCloudApi.getAuthenticatedUserFromStore();

    const experimentalFlag = this.getExperimentalFlag(cloudUser);
    const employeeFlag = this.getEmployeeFlag(cloudUser);

    return {
      cloudUser,
      experimentalFlag,
      employeeFlag,
    };
  }

  async refreshAuthenticatedCloudUser() {
    try {
      // To enable offline access we do not care if this fails. Should probably just check if there
      // is an internet connection here and refresh only when there is. After that we should deep compare
      // the user and only update when something has changed. E.g. a Homey was added.
      await athomCloudApi.getAuthenticatedUserFromStore({ $cache: false });
    } catch (error) {
      // This happens on a password change so we log the user out.
      if (error.statusCode === 400 && error.message === 'Invalid refresh token') {
        await this.logout();
        return;
      }
      console.error('Failed to refreshAuthenticatedUser', error);
    }
  }

  async resolve() {
    if (import.meta.env.MODE === 'self-hosted') {
      const token = sessionStorage.getItem('HOMEY_LOCAL_TOKEN');

      // Redirect to login page.
      if (token == null) {
        history.replace(`${import.meta.env.BASE_URL}login-local`, {
          from: history.location.pathname,
        });
        // TODO
        // if search append...
        return;
      }

      this.set({
        resolved: true,
        loading: false,
      });
      selectedHomeyStore
        .connectToLocalApiHomey()
        .then(() => {})
        .catch(console.error);
      return;
    }

    const isLoggedIn = await athomCloudApi.isLoggedIn();

    // If not logged in resolve so that redirect logic to login page can run.
    if (isLoggedIn !== true) {
      this.set({
        resolved: true,
        loading: false,
      });
      return;
    }

    const init = async () => {
      const authenticatedUserState = await this.getAuthenticatedCloudUserState();

      this.handleUser(authenticatedUserState.cloudUser);

      unstable_batchedUpdates(() => {
        this.set({
          ...authenticatedUserState,
          resolved: true,
          loading: false,
        });
      });

      await this.refreshAuthenticatedCloudUser();
      // TODO
      // compare changes and update store if needed
    };

    try {
      await init();
    } catch (error) {
      console.log({ error });

      switch (error.message) {
        case 'Invalid refresh token':
          await this.logout();
          break;
        default:
      }

      await init();
    }
  }

  async authenticateWithAuthorizationCode(code) {
    try {
      await athomCloudApi.authenticateWithAuthorizationCode(code);
      const authenticatedUserState = await this.getAuthenticatedCloudUserState();

      this.handleUser(authenticatedUserState.cloudUser);

      unstable_batchedUpdates(() => {
        this.set({ ...authenticatedUserState, loading: false });
      });

      return authenticatedUserState.cloudUser;
    } catch (error) {
      this.set({
        cloudUser: null,
        experimentalFlag: false,
        employeeFlag: false,
        error,
      });
    }
  }

  async logout() {
    if (import.meta.env.MODE === 'self-hosted') {
      sessionStorage.removeItem('HOMEY_LOCAL_TOKEN');

      history.push(`${import.meta.env.BASE_URL}login-local`, {
        from: history.location.pathname,
      });
      selectedHomeyStore.reset();
      // https://github.com/pmndrs/zustand/issues/302
      unstable_batchedUpdates(() => {
        this.set({ ...this.createInitialState(), resolved: true });
      });

      return;
    }

    this.handleUser(null);

    await athomCloudApi.logout();

    localStorage.removeItem('current_homey_id');

    // https://github.com/pmndrs/zustand/issues/302
    unstable_batchedUpdates(() => {
      this.set({ ...this.createInitialState(), resolved: true });
    });
  }

  getCurrentHomeyId(cloudUser) {
    const id = localStorage.getItem('current_homey_id');
    let homey = null;

    if (id != null) {
      homey = cloudUser.homeys?.find((homey) => {
        return homey.id === id;
      });
    } else {
      homey = cloudUser.homeys?.[0];
    }

    return homey?.id ?? null;
  }

  getCurrentHomey(cloudUser) {
    const id = localStorage.getItem('current_homey_id');
    let homey = null;

    if (id != null) {
      homey = cloudUser.homeys?.find((homey) => {
        return homey.id === id;
      });
    } else {
      homey = cloudUser.homeys?.[0];
    }

    if (homey == null) {
      localStorage.removeItem('current_homey_id');
      throw new HomeyError.NotFound();
    }

    return homey;
  }

  getCurrentHomeyById(cloudUser, id) {
    localStorage.setItem('current_homey_id', id);

    const homey = cloudUser.homeys?.find((homey) => {
      return homey.id === id;
    });

    if (homey == null) {
      localStorage.removeItem('current_homey_id');
      throw new HomeyError.NotFound();
    }

    return homey;
  }

  getLoginUrl(location) {
    let state = location.pathname;

    if (location.search) {
      state += '?' + location.search;
    }

    if (location.hash) {
      state += '#' + location.hash;
    }

    return athomCloudApi.getLoginUrl({
      state,
      scopes: ['homey', 'account', 'resource', 'homey_local_login'],
    });
  }

  getExperimentalFlag(cloudUser) {
    const isTesting = window.location.hostname === 'my.homey.wtf';
    const isPlayground = window.location.hostname === 'my.homey.fun';

    if (import.meta.env.MODE === 'development' || isTesting || isPlayground) {
      return true;
    }

    return (
      cloudUser != null &&
      (cloudUser.roleIds?.includes('aatp') || cloudUser.roleIds?.includes('employee'))
    );
  }

  getEmployeeFlag(cloudUser) {
    const isTesting = window.location.hostname === 'my.homey.wtf';
    const isPlayground = window.location.hostname === 'my.homey.fun';

    if (import.meta.env.MODE === 'development' || isTesting || isPlayground) {
      return true;
    }

    return cloudUser != null && cloudUser.roleIds?.includes('employee');
  }

  handleUser(cloudUser) {
    if (history.location != null && cloudUser != null) {
      const match = matchPath(
        history.location.pathname,
        `${import.meta.env.BASE_URL}homeys/:homeyId`,
      );

      let getCurrentHomey = null;

      if (match != null) {
        const homeyId = match.params.homeyId;

        getCurrentHomey = () => {
          return authStore.getCurrentHomeyById(cloudUser, homeyId);
        };
      } else {
        getCurrentHomey = () => {
          return authStore.getCurrentHomey(cloudUser);
        };
      }

      try {
        const currentHomey = getCurrentHomey();

        if (currentHomey.id === authStore.currentHomeyId) {
          // debug('Homey ID was the same aborting.');
          return;
        }

        authStore.currentHomeyId = currentHomey.id;

        selectedHomeyStore
          .connectToCloudApiHomey(currentHomey)
          .then(() => {})
          .catch(console.error);
      } catch (error) {
        authStore.set({ error });
        return;
      }
    }

    if (cloudUser == null) {
      selectedHomeyStore.reset();
    }
  }
}

export const authStore = new AuthStore();

authStore.resolve().catch(console.error);

export function useAuth() {
  return authStore.store((state) => state);
}

history.listen((location, action) => {
  if (import.meta.env.MODE === 'self-hosted') {
    return;
  }

  const match = matchPath(location.pathname, `${import.meta.env.BASE_URL}homeys/:homeyId`);

  if (match != null) {
    const homeyId = match.params.homeyId;

    if (authStore.currentHomeyId !== homeyId) {
      authStore.handleUser(authStore.get().cloudUser);
    }
  }
});
